import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { platformAPI, getApiOrigin } from '../../services/api';
import { LoadingSpinner } from '../../components/ui';
import {
  IncidentStatusBadge,
  IncidentSeverityBadge,
  IncidentCategoryBadge,
  SlugBadge,
} from '../../components/platform';

type IncidentRow = {
  id: string;
  tenantId: string;
  category: string;
  title: string;
  description: string;
  module: string;
  severity: string;
  status: string;
  reproductionSteps: string | null;
  pageUrl: string | null;
  browserInfo: string | null;
  errorMessage: string | null;
  stackTrace: string | null;
  attachmentUrl: string | null;
  attachmentOriginalName: string | null;
  ownerNotes: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  tenant: { id: string; name: string; slug: string };
  reportedBy: { id: string; name: string; email: string; role: string };
  resolvedBy: { id: string; name: string; email: string } | null;
};

const statusOptions = [
  { value: '', label: 'Todos los estados' },
  { value: 'NEW', label: 'Nuevo' },
  { value: 'REVIEWING', label: 'En revisión' },
  { value: 'IN_PROGRESS', label: 'En curso' },
  { value: 'RESOLVED', label: 'Resuelto' },
  { value: 'CLOSED', label: 'Cerrado' },
  { value: 'REJECTED', label: 'Rechazado' },
];

const severityOptions = [
  { value: '', label: 'Todas' },
  { value: 'LOW', label: 'Baja' },
  { value: 'MEDIUM', label: 'Media' },
  { value: 'HIGH', label: 'Alta' },
  { value: 'CRITICAL', label: 'Crítica' },
];

const PlatformIncidents: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [tenantId, setTenantId] = useState('');
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const listParams = useMemo(() => {
    const p: Record<string, string | number> = { page, limit: 25 };
    if (tenantId) p.tenantId = tenantId;
    if (status) p.status = status;
    if (severity) p.severity = severity;
    if (moduleFilter.trim()) p.module = moduleFilter.trim();
    if (q.trim()) p.q = q.trim();
    if (dateFrom) p.dateFrom = dateFrom;
    if (dateTo) p.dateTo = dateTo;
    return p;
  }, [tenantId, status, severity, moduleFilter, q, dateFrom, dateTo, page]);

  const { data: tenantsRes } = useQuery(['platform-tenants-all'], () =>
    platformAPI.listTenants({})
  );
  const tenants = (tenantsRes?.data?.data || []) as { id: string; name: string; slug: string }[];

  const { data: listRes, isLoading } = useQuery(
    ['platform-incidents', listParams],
    () => platformAPI.listIncidentReports(listParams),
    { keepPreviousData: true }
  );

  const items = (listRes?.data?.items || []) as IncidentRow[];
  const pagination = listRes?.data?.pagination as
    | { page: number; limit: number; total: number; totalPages: number }
    | undefined;

  const selected = items.find((i) => i.id === selectedId) || null;

  const { data: detailRes } = useQuery(
    ['platform-incident', selectedId],
    () => platformAPI.getIncidentReport(selectedId!),
    { enabled: !!selectedId, staleTime: 30_000 }
  );
  const detail = (detailRes?.data?.data as IncidentRow | undefined) || selected;

  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');

  useEffect(() => {
    const rid = searchParams.get('report');
    const qsTenantId = searchParams.get('tenantId');
    if (rid) setSelectedId(rid);
    if (qsTenantId) setTenantId(qsTenantId);
  }, [searchParams]);

  useEffect(() => {
    if (detail) {
      setEditStatus(detail.status);
      setEditNotes(detail.ownerNotes || '');
    }
  }, [detail?.id, detail?.status, detail?.ownerNotes]);

  const patchMutation = useMutation(
    ({ id, body }: { id: string; body: { status?: string; ownerNotes?: string | null } }) =>
      platformAPI.patchIncidentReport(id, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['platform-incidents']);
        queryClient.invalidateQueries(['platform-incident']);
        toast.success('Cambios guardados');
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || 'Error al guardar');
      },
    }
  );

  const openRow = (id: string) => {
    setSelectedId(id);
    const next = new URLSearchParams(searchParams);
    next.set('report', id);
    if (tenantId) next.set('tenantId', tenantId);
    setSearchParams(next);
  };

  const closeDetail = () => {
    setSelectedId(null);
    const next = new URLSearchParams(searchParams);
    next.delete('report');
    if (!tenantId) next.delete('tenantId');
    setSearchParams(next);
  };

  const saveDetail = () => {
    if (!detail) return;
    const body: { status?: string; ownerNotes?: string } = {};
    if (editStatus !== detail.status) body.status = editStatus;
    if (editNotes !== (detail.ownerNotes || '')) body.ownerNotes = editNotes;
    if (Object.keys(body).length === 0) {
      toast('Sin cambios que guardar');
      return;
    }
    patchMutation.mutate({ id: detail.id, body });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 tracking-tight">Incidencias y soporte</h2>
        <p className="mt-1 text-sm text-slate-600">
          Reportes enviados por usuarios de las funerarias. Revisa el detalle, cambia el estado y
          deja notas internas.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
        <div className="xl:col-span-2 space-y-4">
          <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm shadow-slate-200/50">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Funeraria</label>
                <select
                  value={tenantId}
                  onChange={(e) => {
                    const nextTenant = e.target.value;
                    setTenantId(nextTenant);
                    const next = new URLSearchParams(searchParams);
                    if (nextTenant) next.set('tenantId', nextTenant);
                    else next.delete('tenantId');
                    setSearchParams(next);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm bg-white"
                >
                  <option value="">Todas</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Estado</label>
                <select
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm bg-white"
                >
                  {statusOptions.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Severidad</label>
                <select
                  value={severity}
                  onChange={(e) => {
                    setSeverity(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm bg-white"
                >
                  {severityOptions.map((o) => (
                    <option key={o.value || 'alls'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Módulo</label>
                <input
                  type="text"
                  value={moduleFilter}
                  onChange={(e) => {
                    setModuleFilter(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm"
                  placeholder="p. ej. invoices"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Desde</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Buscar</label>
                <input
                  type="search"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setPage(1);
                  }}
                  className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm"
                  placeholder="Título o descripción"
                />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center text-sm text-slate-500">No hay reportes con estos filtros.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50/90">
                    <tr>
                      <th className="px-3 py-2.5 text-left font-semibold text-slate-700">Fecha</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-slate-700">Funeraria</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-slate-700">Título</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-slate-700">Tipo</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-slate-700">Severidad</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-slate-700">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => openRow(row.id)}
                        className={`cursor-pointer hover:bg-slate-50/80 ${
                          selectedId === row.id ? 'bg-sky-50/60' : ''
                        }`}
                      >
                        <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">
                          {new Date(row.createdAt).toLocaleString('es-VE', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </td>
                        <td className="px-3 py-2.5 max-w-[140px]">
                          <span className="font-medium text-slate-800 truncate block">{row.tenant.name}</span>
                        </td>
                        <td className="px-3 py-2.5 max-w-[220px] text-slate-800 truncate">{row.title}</td>
                        <td className="px-3 py-2.5">
                          <IncidentCategoryBadge category={row.category} />
                        </td>
                        <td className="px-3 py-2.5">
                          <IncidentSeverityBadge severity={row.severity} />
                        </td>
                        <td className="px-3 py-2.5">
                          <IncidentStatusBadge status={row.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-600">
                <span>
                  Página {pagination.page} de {pagination.totalPages} ({pagination.total} total)
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-40"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={page >= pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-40"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm min-h-[320px] xl:sticky xl:top-4">
          {!detail ? (
            <div className="p-8 text-center text-sm text-slate-500">
              Selecciona un reporte en la tabla para ver el detalle y el seguimiento.
            </div>
          ) : (
            <div className="p-5 space-y-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-base font-semibold text-slate-900 leading-snug">{detail.title}</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {new Date(detail.createdAt).toLocaleString('es-VE', {
                      dateStyle: 'full',
                      timeStyle: 'short',
                    })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeDetail}
                  className="text-xs font-semibold text-slate-500 hover:text-slate-800"
                >
                  Cerrar
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                <IncidentCategoryBadge category={detail.category} />
                <IncidentSeverityBadge severity={detail.severity} />
                <IncidentStatusBadge status={detail.status} />
              </div>

              <div className="text-sm space-y-1 border border-slate-100 rounded-xl p-3 bg-slate-50/50">
                <p>
                  <span className="text-slate-500">Funeraria:</span>{' '}
                  <span className="font-medium text-slate-800">{detail.tenant.name}</span>{' '}
                  <SlugBadge>{detail.tenant.slug}</SlugBadge>
                </p>
                <p>
                  <span className="text-slate-500">Reportante:</span>{' '}
                  <span className="font-medium text-slate-800">{detail.reportedBy.name}</span>{' '}
                  <span className="text-slate-600">&lt;{detail.reportedBy.email}&gt;</span>
                </p>
                <p>
                  <span className="text-slate-500">Módulo:</span>{' '}
                  <code className="text-xs bg-white px-1.5 py-0.5 rounded border border-slate-200">
                    {detail.module}
                  </code>
                </p>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
                  Descripción
                </h4>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{detail.description}</p>
              </div>

              {detail.reproductionSteps && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
                    Pasos para reproducir
                  </h4>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{detail.reproductionSteps}</p>
                </div>
              )}

              {detail.pageUrl && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
                    Página
                  </h4>
                  <p className="text-xs text-sky-700 break-all">{detail.pageUrl}</p>
                </div>
              )}

              {detail.browserInfo && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
                    Navegador / user agent
                  </h4>
                  <p className="text-xs text-slate-600 break-all font-mono">{detail.browserInfo}</p>
                </div>
              )}

              {(detail.errorMessage || detail.stackTrace) && (
                <details className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                  <summary className="cursor-pointer font-medium text-slate-700">Detalles técnicos</summary>
                  {detail.errorMessage && (
                    <p className="mt-2 text-xs text-rose-800 whitespace-pre-wrap">{detail.errorMessage}</p>
                  )}
                  {detail.stackTrace && (
                    <pre className="mt-2 text-[11px] text-slate-700 overflow-x-auto whitespace-pre-wrap">
                      {detail.stackTrace}
                    </pre>
                  )}
                </details>
              )}

              {detail.attachmentUrl && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
                    Adjunto
                  </h4>
                  <a
                    href={`${getApiOrigin()}${detail.attachmentUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-sky-700 hover:underline"
                  >
                    {detail.attachmentOriginalName || 'Ver archivo'}
                  </a>
                </div>
              )}

              <div className="border-t border-slate-100 pt-4 space-y-3">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Seguimiento</h4>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Estado</label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm bg-white"
                  >
                    {statusOptions
                      .filter((o) => o.value)
                      .map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Notas internas (solo plataforma)</label>
                  <textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    rows={4}
                    className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm"
                    placeholder="Comentarios para tu equipo; no visibles en la funeraria."
                  />
                </div>
                {detail.resolvedAt && (
                  <p className="text-xs text-slate-500">
                    Resuelto{' '}
                    {new Date(detail.resolvedAt).toLocaleString('es-VE', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                    {detail.resolvedBy && ` · ${detail.resolvedBy.name}`}
                  </p>
                )}
                <button
                  type="button"
                  onClick={saveDetail}
                  disabled={patchMutation.isLoading}
                  className="w-full rounded-xl bg-slate-900 text-white text-sm font-semibold py-2.5 hover:bg-slate-800 disabled:opacity-60"
                >
                  {patchMutation.isLoading ? 'Guardando…' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlatformIncidents;
